import { useState, useRef } from 'react'
import {
  CloseButton,
  Button,
  VStack,
  HStack,
  Input,
  Textarea,
  Switch,
  NativeSelect,
  InputGroup,
  IconButton,
  Box,
  Text,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiX } from 'react-icons/fi'
import { useForm } from 'react-hook-form'
import { useCreateEmployment, useUpdateEmployment } from '../../hooks/useEmployment'
import { useWorksiteSearch } from '../../hooks/useBusiness'
import { useUnionSearch } from '../../hooks/useUnion'
import { WorkScheduleEditor, parseScheduleJson, scheduleToJson } from './WorkScheduleEditor'
import type { WeekSchedule } from './WorkScheduleEditor'
import type { SaveEmploymentRequest } from '../../types/employment'
import type { ContactEmployment } from '../../types/contactDetail'

interface EmploymentFormModalProps {
  isOpen: boolean
  onClose: () => void
  contactId: string
  employment?: ContactEmployment
}

export function EmploymentFormModal({ isOpen, onClose, contactId, employment }: EmploymentFormModalProps) {
  const isEdit = !!employment

  const createMutation = useCreateEmployment(contactId)
  const updateMutation = useUpdateEmployment(contactId)

  // Worksite combo box state
  const [selectedWorksite, setSelectedWorksite] = useState<{ id: string; label: string } | null>(
    employment ? { id: employment.worksiteId, label: employment.worksiteName } : null
  )
  const [worksiteInput, setWorksiteInput] = useState(employment?.worksiteName ?? '')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [worksiteSearch, setWorksiteSearch] = useState(employment?.worksiteName ?? '')
  const { data: worksiteData } = useWorksiteSearch({ search: worksiteSearch || undefined, pageSize: 50 })

  // Union data
  const { data: unionData } = useUnionSearch({ pageSize: 100 })

  // Work schedule state (managed separately from react-hook-form since it's complex)
  const [workSchedule, setWorkSchedule] = useState<WeekSchedule>(
    () => parseScheduleJson(employment?.workScheduleJson)
  )

  const { register, handleSubmit, watch, setValue, formState: { isSubmitting } } = useForm<SaveEmploymentRequest>({
    defaultValues: {
      worksiteId: employment?.worksiteId ?? '',
      position: employment?.position ?? '',
      department: employment?.department ?? '',
      startDate: employment?.startDate?.split('T')[0] ?? '',
      endDate: employment?.endDate?.split('T')[0] ?? '',
      isActive: employment?.isActive ?? true,
      isPrimary: employment?.isPrimary ?? false,
      unionId: employment?.unionId ?? undefined,
      unionMemberNumber: employment?.unionMemberNumber ?? '',
      notes: '',
    },
  })

  const isActive = watch('isActive')
  const isPrimary = watch('isPrimary')
  const worksiteId = watch('worksiteId')

  const onSubmit = async (data: SaveEmploymentRequest) => {
    if (!worksiteId) {
      toaster.create({ title: 'Please select a worksite', type: 'warning', duration: 3000 })
      return
    }

    // Clean up empty strings to null for optional fields
    const cleanData: SaveEmploymentRequest = {
      ...data,
      position: data.position || undefined,
      department: data.department || undefined,
      startDate: data.startDate || undefined,
      endDate: data.endDate || undefined,
      unionId: data.unionId || null,
      unionMemberNumber: data.unionMemberNumber || undefined,
      workScheduleJson: scheduleToJson(workSchedule),
      notes: data.notes || undefined,
    }

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id: employment.employmentId, data: cleanData })
        toaster.create({ title: 'Employment updated', type: 'success', duration: 3000 })
      } else {
        await createMutation.mutateAsync(cleanData)
        toaster.create({ title: 'Employment created', type: 'success', duration: 3000 })
      }
      onClose()
    } catch {
      toaster.create({ title: `Failed to ${isEdit ? 'update' : 'create'} employment`, type: 'error', duration: 3000 })
    }
  }

  const handleWorksiteInputChange = (value: string) => {
    setWorksiteInput(value)
    setSelectedWorksite(null)
    setValue('worksiteId', '')
    setWorksiteSearch(value)
    setIsDropdownOpen(true)
  }

  const handleSelectWorksite = (id: string, label: string, city?: string) => {
    const displayLabel = city ? `${label} (${city})` : label
    setSelectedWorksite({ id, label: displayLabel })
    setWorksiteInput(displayLabel)
    setValue('worksiteId', id)
    setIsDropdownOpen(false)
  }

  const handleClearWorksite = () => {
    setSelectedWorksite(null)
    setWorksiteInput('')
    setValue('worksiteId', '')
    setWorksiteSearch('')
  }

  return (
    <Dialog.Root open={isOpen} size='lg' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Dialog.Header>{isEdit ? 'Edit Employment' : 'Add Employment'}</Dialog.Header>
              <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>

              <Dialog.Body>
                <VStack gap={4}>
                  {/* Worksite combo box */}
                  <Field.Root required>
                    <Field.Label fontSize="sm">Worksite</Field.Label>
                    <Box position="relative">
                      <InputGroup endElement={selectedWorksite && (
                        <IconButton
                          size="xs"
                          variant="ghost"
                          aria-label="Clear worksite"
                          onClick={handleClearWorksite}><FiX /></IconButton>
                      )}>
                        <Input
                          size="sm"
                          value={worksiteInput}
                          onChange={e => handleWorksiteInputChange(e.target.value)}
                          onFocus={() => { if (!selectedWorksite) setIsDropdownOpen(true) }}
                          onBlur={() => {
                            if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
                            blurTimeoutRef.current = setTimeout(() => setIsDropdownOpen(false), 200)
                          }}
                          placeholder="Type to search worksites..."
                          bg={selectedWorksite ? 'accent.soft' : undefined}
                          readOnly={!!selectedWorksite}
                        />
                      </InputGroup>
                      {isDropdownOpen && !selectedWorksite && worksiteData?.items && worksiteData.items.length > 0 && (
                        <Box
                          position="absolute"
                          zIndex={10}
                          w="full"
                          bg="surface"
                          border="1px"
                          borderColor="gray.200"
                          borderRadius="md"
                          shadow="md"
                          maxH="200px"
                          overflowY="auto"
                          mt={1}
                        >
                          {worksiteData.items.map(w => (
                            <Box
                              key={w.id}
                              px={3}
                              py={2}
                              cursor="pointer"
                              _hover={{ bg: 'gray.50' }}
                              fontSize="sm"
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => handleSelectWorksite(w.id, w.label, w.city)}
                            >
                              <Text fontWeight="medium">{w.label}</Text>
                              <Text fontSize="xs" color="gray.500">
                                {[w.city, w.state].filter(Boolean).join(', ')}
                                {w.type ? ` · ${w.type}` : ''}
                              </Text>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Field.Root>

                  <Field.Root>
                    <Field.Label fontSize="sm">Position</Field.Label>
                    <Input size="sm" {...register('position')} placeholder="e.g., Manager" />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label fontSize="sm">Department</Field.Label>
                    <Input size="sm" {...register('department')} placeholder="e.g., Produce" />
                  </Field.Root>

                  <HStack gap={4} w="full">
                    <Field.Root>
                      <Field.Label fontSize="sm">Start Date</Field.Label>
                      <Input size="sm" type="date" {...register('startDate')} />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label fontSize="sm">End Date</Field.Label>
                      <Input size="sm" type="date" {...register('endDate')} />
                    </Field.Root>
                  </HStack>

                  {/* Work schedule editor */}
                  <WorkScheduleEditor value={workSchedule} onChange={setWorkSchedule} />

                  {/* Union picker */}
                  <Field.Root>
                    <Field.Label fontSize="sm">Union</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field {...register('unionId')} placeholder="No union">
                        {unionData?.items.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.fullName || u.name} {u.localNumber ? `(Local ${u.localNumber})` : ''}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>

                  <Field.Root>
                    <Field.Label fontSize="sm">Union Member Number</Field.Label>
                    <Input size="sm" {...register('unionMemberNumber')} />
                  </Field.Root>

                  <HStack gap={8} w="full">
                    <Field.Root display="flex" alignItems="center">
                      <Field.Label htmlFor="isActive" mb={0} fontSize="sm">Active</Field.Label>
                      <Switch.Root
                        id="isActive"
                        checked={isActive}
                        onCheckedChange={e => setValue('isActive', e.checked === true)}
                        colorPalette="green"
                      >
                        <Switch.HiddenInput />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    </Field.Root>
                    <Field.Root display="flex" alignItems="center">
                      <Field.Label htmlFor="isPrimary" mb={0} fontSize="sm">Primary</Field.Label>
                      <Switch.Root
                        id="isPrimary"
                        checked={isPrimary}
                        onCheckedChange={e => setValue('isPrimary', e.checked === true)}
                        colorPalette="brand"
                      >
                        <Switch.HiddenInput />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    </Field.Root>
                  </HStack>

                  <Field.Root>
                    <Field.Label fontSize="sm">Notes</Field.Label>
                    <Textarea size="sm" {...register('notes')} rows={2} />
                  </Field.Root>
                </VStack>
              </Dialog.Body>

              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                <Button colorPalette="brand" type="submit" loading={isSubmitting}>
                  {isEdit ? 'Save Changes' : 'Add Employment'}
                </Button>
              </Dialog.Footer>
            </form>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
